import type Big from 'big.js';
import type { BigSource } from 'big.js';
import { toBig } from './number';
import { SpreadUnit } from './security';

interface GetSpreadOptions {
  /** Return true if the bid and ask provided are relative numbers (in % space). Defaults to false (absolute price terms) */
  isRelativePrice?: boolean;
}

/**
 * Given a bid and ask price, get the spread in percentage terms. If your prices are already in percentage terms
 * (eg: bid -0.01, ask +0.01, meaning 2% spread), pass options.isRelativePrice = true.
 */
export function getSpreadPct(
  bidPrice: BigSource,
  askPrice: BigSource,
  options: GetSpreadOptions = {}
): Big | undefined {
  const bid = toBig(bidPrice);
  const ask = toBig(askPrice);

  if (!bid || !ask) {
    // We can't calculate a spread
    return undefined;
  }

  if (options.isRelativePrice) {
    return ask.minus(bid).times(100);
  }

  const askPlusBid = ask.plus(bid);
  // Make sure we dont divide by zero
  if (askPlusBid.eq(0)) {
    return undefined;
  }

  // ((ask - bid) / ((ask + bid) / 2)) -- Note that this is the _mid-point_ relative spread formula
  return ask.minus(bid).div(askPlusBid.div(2)).times(100);
}

/**
 * Returns the result of `getSpreadPct`, and adds some display logic
 * If the spread pct > 10%, will return x%, else just returns x (bps)
 */
export function getSpreadPctDisplay(bidPrice: BigSource, askPrice: BigSource, options: GetSpreadOptions = {}): string {
  const spreadPct = getSpreadPct(bidPrice, askPrice, options);
  if (!spreadPct) {
    // Wasn't able to calculate the spread. This function returns a string to display. Go empty string.
    return '';
  }

  // If result > 1000 BPS, return as %
  return spreadPct.abs().gt(10) ? spreadPct.toFixed(2) + '%' : spreadPct.times(100).toFixed(2);
}

/** Get price diff in absolute terms */
export function getPriceDiff(bidPrice: string, askPrice: string, decimalPlaces = 2): string {
  const bid = parseFloat(bidPrice);
  const ask = parseFloat(askPrice);
  const priceDifference = ask - bid;
  return Math.abs(priceDifference).toFixed(decimalPlaces);
}

/**
 * Get the spread display value between bid and offer prices.
 * - When prices are in cash terms and spread unit is BPS: calculate spread as BPS (or % when very wide)
 * - When prices are already in BPS (isPricingModeBPS): use absolute difference
 * - When differenceUnit is PRICE: use absolute cash difference
 */
export function getSpreadDisplayValue(
  bidPrice: string,
  offerPrice: string,
  differenceUnit: SpreadUnit,
  isPricingModeBPS: boolean
): string {
  const shouldShowPercentageSpread = differenceUnit === SpreadUnit.BPS && !isPricingModeBPS;
  return shouldShowPercentageSpread ? getSpreadPctDisplay(bidPrice, offerPrice) : getPriceDiff(bidPrice, offerPrice);
}
